import Button from "components/shared/button";
import { Popup } from "components/popups/Popup";
import React, { useEffect, useMemo, useState } from "react";
import { Table } from "antd";
import Input from "components/shared/inputs/input";
import Select from "components/shared/inputs/select";
import useTranslation from "utils/hooks/useTranslation";
import useCustomForm from "utils/hooks/useCustomForm";
import { useUpdateAccessoryMutation, useLazyGetAccessoryDepIncQuery } from "store/api";
import type { DatasourceMutationRequest } from "./TvaPopup";

type EditableAccessoryRow = {
  id?: number | string;
  key?: number | string;
  dep?: (string | number)[];
  inc?: (string | number)[];
  accessory_reference?: string;
  reference_def?: string;
  accessory_code?: string;
  code?: string;
  accessory_price?: number | string;
  accessory_ecoprice?: number | string;
  accessory_photo?: string;
  group?: string;
  regulation_thermostat?: boolean | null;
  regulation_sensor?: boolean | null;
  description?: string;
  description_bc?: string;
};

type DepIncRow = { id: number | string; code: string; reference?: string };

type AccessoryEditPopupProps = {
  isOpen: boolean;
  onClose: () => void;
  request: DatasourceMutationRequest | null;
  envNumeric: number | undefined;
  lang: string;
  row?: EditableAccessoryRow | null;
  onSaved?: () => Promise<void> | void;
};

type AccessoryRelationSectionProps = {
  title: string;
  items: DepIncRow[];
  emptyLabel: string;
  codeLabel: string;
  referenceLabel: string;
};

const boolToStr = (v?: boolean | null) => (v == null ? "false" : String(v));
const strToBool = (v: string) => v === "true";

function resolveDepInc(ids: (string | number)[] | undefined, all: DepIncRow[]): DepIncRow[] {
  if (!ids) return [];
  const result: DepIncRow[] = [];
  for (const id of ids) {
    const found = all.find((a) => String(a.id) === String(id));
    if (found) result.push(found);
  }
  return result;
}

const AccessoryRelationSection: React.FC<AccessoryRelationSectionProps> = ({
  title,
  items,
  emptyLabel,
  codeLabel,
  referenceLabel,
}) => {
  const dataSource = items.map((item) => ({
    key: String(item.id),
    code: item.code,
    reference: item.reference ?? "",
  }));

  const columns = [
    { title: codeLabel, dataIndex: "code", key: "code", width: 180 },
    { title: referenceLabel, dataIndex: "reference", key: "reference" },
  ];

  return (
    <group data-direction="column" data-fit="1" data-gap="8">
      <text data-weight="700">{title}</text>
      <Table
        size="small"
        pagination={false}
        columns={columns}
        dataSource={dataSource}
        locale={{ emptyText: emptyLabel }}
      />
    </group>
  );
};

const AccessoryEditPopup: React.FC<AccessoryEditPopupProps> = ({
  isOpen,
  onClose,
  request,
  envNumeric,
  lang,
  row,
  onSaved,
}) => {
  const { t } = useTranslation();
  const yesNoOptions = useMemo(
    () => [
      { value: "true", text: t("yes") },
      { value: "false", text: t("no") },
    ],
    [t]
  );
  const [isSaving, setIsSaving] = useState(false);
  const [depAcc, setDepAcc] = useState<DepIncRow[]>([]);
  const [incAcc, setIncAcc] = useState<DepIncRow[]>([]);
  const [updateAccessory] = useUpdateAccessoryMutation();
  const [fetchDepInc] = useLazyGetAccessoryDepIncQuery();

  const { control, reset, getValues, watch, setError, clearErrors } = useCustomForm<{
    accessory_reference: string;
    accessory_code: string;
    accessory_price: string;
    accessory_ecoprice: string;
    accessory_photo: string;
    regulation_thermostat: string;
    regulation_sensor: string;
    description: string;
    description_bc: string;
  }>({
    defaultValues: {
      accessory_reference: "",
      accessory_code: "",
      accessory_price: "",
      accessory_ecoprice: "",
      accessory_photo: "",
      regulation_thermostat: "false",
      regulation_sensor: "false",
      description: "",
      description_bc: "",
    },
  });

  const watchThermostat = watch("regulation_thermostat");
  const watchSensor = watch("regulation_sensor");

  useEffect(() => {
    setIsSaving(false);
    setDepAcc([]);
    setIncAcc([]);

    if (!isOpen || !row) return;

    reset({
      accessory_reference: String(row.accessory_reference ?? ""),
      accessory_code: String(row.accessory_code ?? ""),
      accessory_price: String(row.accessory_price ?? ""),
      accessory_ecoprice: String(row.accessory_ecoprice ?? ""),
      accessory_photo: String(row.accessory_photo ?? ""),
      regulation_thermostat: boolToStr(row.regulation_thermostat),
      regulation_sensor: boolToStr(row.regulation_sensor),
      description: String(row.description ?? ""),
      description_bc: String(row.description_bc ?? ""),
    });

    if (row.code && envNumeric != null) {
      fetchDepInc({ code: row.code, env: String(envNumeric) })
        .then((result) => {
          const data = result.data;
          if (!data) return;
          const allAcc: DepIncRow[] = Array.isArray(data) ? data : [];
          setDepAcc(resolveDepInc(row.dep, allAcc));
          setIncAcc(resolveDepInc(row.inc, allAcc));
        })
        .catch(() => { /* silent */ });
    }
  }, [isOpen, reset, row, envNumeric, fetchDepInc]);

  const handleSave = async () => {
    if (!request) return;
    const rowId = row?.id ?? row?.key;
    if (rowId == null) return;

    const {
      accessory_reference, accessory_code, accessory_price, accessory_ecoprice,
      accessory_photo, regulation_thermostat, regulation_sensor, description, description_bc,
    } = getValues();

    const normalizedDescription = String(description ?? "").trim();
    const normalizedDescriptionBc = String(description_bc ?? "").trim();

    let hasValidationError = false;
    if (!normalizedDescription) {
      setError("description", { type: "required", message: t("Required") });
      hasValidationError = true;
    }
    if (!normalizedDescriptionBc) {
      setError("description_bc", { type: "required", message: t("Required") });
      hasValidationError = true;
    }
    if (hasValidationError) return;

    clearErrors(["description", "description_bc"]);

    const baseUrl = request.url.replace(/\/[^/]+$/, "");
    const url = `${baseUrl}/${rowId}`;

    const payload: Record<string, unknown> = {
      id: rowId,
      accessory_reference,
      accessory_code,
      lang,
      reference_def: row?.reference_def,
      accessory_price,
      accessory_ecoprice,
      description: normalizedDescription,
      description_bc: normalizedDescriptionBc,
      code: row?.code,
      group: row?.group,
      accessory_photo,
      regulation_thermostat: strToBool(regulation_thermostat),
      regulation_sensor: strToBool(regulation_sensor),
    };

    setIsSaving(true);
    try {
      await updateAccessory({ url, data: payload }).unwrap();
      onSaved?.();
      onClose();
    } catch {
      // handled by api interceptor
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Popup
      title={t("Accessory")}
      onClose={onClose}
      portal
      fixSize="large"
      closeOnOutsideClick
      commandPanelProps={{
        additionalElement: (
          <group data-justify="right" data-gap="10">
            <Button type="button" onClick={onClose} disabled={isSaving}>
              {t("Back")}
            </Button>
            <Button type="button" primary onClick={handleSave} disabled={isSaving}>
              {t("Update")}
            </Button>
          </group>
        ),
      }}
    >
      <group
        data-direction="column"
        data-gap="20"
        data-space="40"
        data-space-horizontal="40"
        data-space-top="5"
        data-height="autofit"
      >
        <group data-gap="10" data-space-vertical="20">
          <group data-direction="column" data-gap="20">

            {/* reference / reference_def */}
            <group data-gap="20">
              <group data-length="forcefit" data-fit="1">
                <Input dataLength="autofit" label={t("reference")} name="accessory_reference" control={control} type="text" size="large" />
              </group>
              <group data-length="forcefit" data-fit="1">
                <Input dataLength="autofit" label={t("reference_def")} name="reference_def" control={control as any} type="text" size="large" disabled defaultValue={row?.reference_def ?? ""} />
              </group>
            </group>

            {/* envcode / code */}
            <group data-gap="20">
              <group data-length="forcefit" data-fit="1">
                <Input dataLength="autofit" label={t("envcode")} name="accessory_code" control={control} type="text" size="large" />
              </group>
              <group data-length="forcefit" data-fit="1">
                <Input dataLength="autofit" label={t("code")} name="code" control={control as any} type="text" size="large" disabled defaultValue={row?.code ?? ""} />
              </group>
            </group>

            {/* price / ecoprice */}
            <group data-gap="20">
              <group data-length="forcefit" data-fit="1">
                <Input dataLength="autofit" label={t("price")} name="accessory_price" control={control} type="text" size="large" />
              </group>
              <group data-length="forcefit" data-fit="1">
                <Input dataLength="autofit" label={t("accessory_ecoprice")} name="accessory_ecoprice" control={control} type="text" size="large" />
              </group>
            </group>

            {/* photo / group */}
            <group data-gap="20">
              <group data-length="forcefit" data-fit="1">
                <Input dataLength="autofit" label={t("photo")} name="accessory_photo" control={control} type="text" size="large" />
              </group>
              <group data-length="forcefit" data-fit="1">
                <Input dataLength="autofit" label={t("group")} name="group" control={control as any} type="text" size="large" disabled defaultValue={row?.group ?? ""} />
              </group>
            </group>

            {/* thermostat / sensor */}
            <group data-gap="20">
              <group data-length="forcefit" data-fit="1">
                <Select
                  dataLength="autofit"
                  label={t("regulation_thermostat")}
                  name="regulation_thermostat"
                  control={control as any}
                  options={yesNoOptions}
                  disabled={watchSensor === "true"}
                  size="large"
                />
              </group>
              <group data-length="forcefit" data-fit="1">
                <Select
                  dataLength="autofit"
                  label={t("regulation_sensor")}
                  name="regulation_sensor"
                  control={control as any}
                  options={yesNoOptions}
                  disabled={watchThermostat === "true"}
                  size="large"
                />
              </group>
            </group>

            {/* description */}
            <group data-gap="20">
              <group data-length="forcefit" data-fit="1">
                <Input dataLength="autofit" label={t("Description_BtoB")} name="description" control={control} type="textarea" size="large" required />
              </group>
            </group>

            <group data-gap="20">
              <group data-length="forcefit" data-fit="1">
                <Input dataLength="autofit" label={t("Description_BtoC")} name="description_bc" control={control} type="textarea" size="large" required />
              </group>
            </group>

            {/* Dep / Inc accessories */}
            <group data-gap="20" data-space-top="10">
              <AccessoryRelationSection
                title={t("Dependant_accessories")}
                items={depAcc}
                emptyLabel={t("No dependant accessories")}
                codeLabel={t("code")}
                referenceLabel={t("reference")}
              />
              <AccessoryRelationSection
                title={t("Invalid_accessories")}
                items={incAcc}
                emptyLabel={t("No invalid accessories")}
                codeLabel={t("code")}
                referenceLabel={t("reference")}
              />
            </group>
          </group>
        </group>
      </group>
    </Popup>
  );
};

export default AccessoryEditPopup;
